import { ReactNode, useContext } from "react";
import { Link } from "react-router-dom";
import { Breadcrumbs as MuiBreadcrumbs } from "@mui/material";
import Icon from "@mui/material/Icon";

import MDBox from "components/MDBox";
import MDTypography from "components/MDTypography";

import { LanguageContext } from "context";
import routes from "routes";

// Props 型定義
interface Props {
  icon: ReactNode;
  title: string;
  route: string | string[];
  light?: boolean;
  [key: string]: any;
}

// 再帰的にルートを flatten
const flattenRoutes = (routes: any[]): any[] =>
  routes.flatMap((r) =>
    Array.isArray(r.collapse) ? [r, ...flattenRoutes(r.collapse)] : [r]
  );

function Breadcrumbs({ icon, title, route, light = false }: Props): JSX.Element {
  const routeArray: string[] = Array.isArray(route) ? route.slice(0, -1) : [];
  const { t } = useContext(LanguageContext) as any;

  const flatRoutes = flattenRoutes(routes);
  const routeTitleMap: Record<string, string> = Object.fromEntries(
    flatRoutes.map((r) => [r.key, r.translationKey || r.name || r.key])
  );

  const tranTitleKey = routeTitleMap[title] || title;
  const tranTitle = t(tranTitleKey);

  return (
    <MDBox
      mr={{ xs: 0, xl: 8 }}
      sx={{
        display: "flex",
        flexDirection: "row",
        alignItems: "center",
        gap: 2, // パンくずと見出しの間の余白
      }}
    >
      <MuiBreadcrumbs
        sx={{
          "& .MuiBreadcrumbs-separator": {
            color: ({ palette: { white, grey } }) =>
              light ? white.main : grey[600],
          },
        }}
      >
        {/* ホームアイコン */}
        <MDTypography
          component={Link}
          to="/"
          variant="body2"
          color={light ? "white" : "dark"}
          opacity={light ? 0.8 : 0.5}
          sx={{ lineHeight: 0, display: "flex", alignItems: "center" }}
        >
          <Icon>{icon}</Icon>
        </MDTypography>

        {/* 渡された route 配列に基づくパンくずリスト */}
        {routeArray.map((key) => {
          const label = routeTitleMap[key] || key;
          const path = flatRoutes.find((r) => r.key === key)?.route || `/${key}`;

          return (
            <MDTypography
              component={Link}
              to={path}
              key={key}
              variant="button"
              fontWeight="regular"
              textTransform="capitalize"
              color={light ? "white" : "dark"}
              opacity={light ? 0.8 : 0.5}
              sx={{ lineHeight: 0 }}
            >
              {t(label)}
            </MDTypography>
          );
        })}

        {/* 現在のページ */}
        <MDTypography
          variant="button"
          fontWeight="regular"
          textTransform="capitalize"
          color={light ? "white" : "dark"}
          sx={{ lineHeight: 0 }}
        >
          {tranTitle.replace("-", " ")}
        </MDTypography>
      </MuiBreadcrumbs>

      {/* 見出し */}
      <MDTypography
        fontWeight="bold"
        textTransform="capitalize"
        variant="h6"
        color={light ? "white" : "dark"}
        noWrap
      >
        {tranTitle.replace("-", " ")}
      </MDTypography>
    </MDBox>
  );
}

export default Breadcrumbs;
